import { css } from '@emotion/react';
import { Clear } from '@mui/icons-material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { IconButton } from '@mui/material';
import { useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { ControllerRenderProps, useFormContext } from 'react-hook-form';
import { IBaseFieldProps } from '../../../../../components/Form/Display/types';
import { FieldFactory } from '../../../../../components/Form/FieldFactory';
import { BasicDragHandle } from '../../../../../components/SortableList/SortableItem';
import { RendererType } from '../../../../../data-models/field-definition';
import { IField, ISimpleChoice } from '../../../../../data-models/field2.data-model';
import {
  CustomKpiTemplateFormValues,
  CustomTemplateSectionType,
} from '../../../../../schemas/kpi-template.schema';
import { colors } from '../../../../../theme/colors';
import { createFormField } from '../../../../../view-models/form.view-model';
import {
  selectedKpiConfigIdAtom,
  showKpiConfigFormAtom,
} from '../../../kpi-config-form/kpi-config-form-state';
import { useDateCustomKpis, useFileCustomKpis, useTextualCustomKpis } from '../hooks/useFilteredKpiConfigs';
import { sectionCardCss, sectionIconSx, SectionTypeIcon } from './section-editor.styles';

const rowCss = css({
  ...sectionCardCss,
  display: 'grid',
  gridTemplateColumns: 'auto auto 1fr auto auto',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.25rem 0.5rem',
});

const SectionTypePlaceholder: Record<CustomTemplateSectionType, string> = {
  [CustomTemplateSectionType.textual]: 'Select Text Question',
  [CustomTemplateSectionType.file]: 'Select File Upload',
  [CustomTemplateSectionType.date]: 'Select Date Field',
  [CustomTemplateSectionType.quantitative]: '',
};

interface SingleKpiSectionRowProps {
  sectionIndex: number;
  sectionType: CustomTemplateSectionType;
  onDelete: () => void;
  onCreateNewKpi?: (sectionIndex: number, sectionType: CustomTemplateSectionType, kpiName?: string) => void;
}

function useSingleKpiSectionRow(sectionIndex: number, sectionType: CustomTemplateSectionType) {
  const { setValue, watch } = useFormContext<CustomKpiTemplateFormValues>();
  const setSelectedKpiConfigId = useSetAtom(selectedKpiConfigIdAtom);
  const setShowKpiConfigForm = useSetAtom(showKpiConfigFormAtom);
  const textualKpis = useTextualCustomKpis();
  const fileKpis = useFileCustomKpis();
  const dateKpis = useDateCustomKpis();

  const kpiIds: number[] = watch(`sections.${sectionIndex}.kpis`);
  const selectedId = kpiIds[0] as number | undefined;

  const availableKpis: IField<unknown>[] = useMemo(() => {
    switch (sectionType) {
      case CustomTemplateSectionType.textual:
        return textualKpis;
      case CustomTemplateSectionType.file:
        return fileKpis;
      case CustomTemplateSectionType.date:
        return dateKpis;
      case CustomTemplateSectionType.quantitative:
        return [];
    }
  }, [sectionType, textualKpis, fileKpis, dateKpis]);

  const handleSelect = useCallback(
    (id: number | null) => {
      if (id == null) {
        return;
      }
      setValue(`sections.${sectionIndex}.kpis`, [id], { shouldDirty: true });
    },
    [setValue, sectionIndex]
  );

  const handleEdit = useCallback(() => {
    if (selectedId == null) {
      return;
    }
    setSelectedKpiConfigId(selectedId);
    setShowKpiConfigForm(true);
  }, [selectedId, setSelectedKpiConfigId, setShowKpiConfigForm]);

  const selectOptions = useMemo<ISimpleChoice<number>[]>(
    () =>
      availableKpis.map((kpi) => ({
        displayName: kpi.displayName,
        value: kpi.id,
      })),
    [availableKpis]
  );

  return { selectedId, selectOptions, handleSelect, handleEdit };
}

export function SingleKpiSectionRow({
  sectionIndex,
  sectionType,
  onDelete,
  onCreateNewKpi,
}: SingleKpiSectionRowProps) {
  const { selectedId, selectOptions, handleSelect, handleEdit } = useSingleKpiSectionRow(
    sectionIndex,
    sectionType
  );

  const Icon = SectionTypeIcon[sectionType];

  const selectorProps = useMemo(() => {
    const formField = createFormField({
      key: 'selectKpi',
      // `createFormField` falls back to the key for a missing label, and the label is what
      // names the combobox — without this, it is announced as "selectKpi".
      label: SectionTypePlaceholder[sectionType],
      renderer: RendererType.singleSelect,
      disableClearable: true,
      rendererMeta: {
        values: selectOptions,
        actionMeta: onCreateNewKpi
          ? {
              actionLabel: '+ Create New KPI',
              onAction: (kpiName?: string) => onCreateNewKpi(sectionIndex, sectionType, kpiName),
            }
          : undefined,
      },
      placeholder: SectionTypePlaceholder[sectionType],
    });

    return {
      formProps: { value: selectedId ?? null, onChange: handleSelect } as ControllerRenderProps,
      formField,
    } as IBaseFieldProps<unknown>;
  }, [selectOptions, selectedId, handleSelect, onCreateNewKpi, sectionIndex, sectionType]);

  return (
    <div css={rowCss}>
      <BasicDragHandle />
      <Icon htmlColor={colors.secondary[40]} aria-hidden='true' sx={sectionIconSx} />
      <FieldFactory {...selectorProps} key={selectOptions.length} />
      <IconButton
        size='small'
        onClick={handleEdit}
        disabled={selectedId == null}
        aria-label='Edit KPI'
        sx={{ visibility: selectedId != null ? 'visible' : 'hidden' }}
      >
        <EditOutlinedIcon fontSize='small' />
      </IconButton>
      <IconButton size='small' onClick={onDelete} aria-label='Delete section'>
        <Clear fontSize='small' />
      </IconButton>
    </div>
  );
}
